import type { LucideIcon } from 'lucide-react'

import {
    ClipboardList,
    HeartHandshake,
    ShieldCheck,
    UsersRound,
    Wallet,
    Sparkles
} from 'lucide-react'

export type Service = {
    id: string
    name: string
    tagline: string
    description: string
    bestFor: string
    leadTime: string
    includes: Array<string>
}

export const SERVICES: Array<Service> = [
    {
        id: 'full-planning',
        name: "Full planning and design",
        tagline: "Every function, every vendor, every guest, from the first venue visit to the vidaai.",
        description: "We take on the whole wedding: venues, design, vendors, budgets, guest hospitality and an on-ground team for every function. You make the decisions that matter, and we take care of everything that makes them happen.",
        bestFor: "Multi-day weddings with 200 or more guests, and families living in different cities or countries.",
        leadTime: "Book 10 to 14 months ahead",
        includes: [
            "Venue scouting and site visits with you",
            "A design concept and decor for every function",
            "Vendor shortlists, negotiation and contracts",
            "Line-by-line budget tracking",
            "Guest list, RSVPs, room blocks and travel",
            "Hospitality desks at every hotel",
            "Ritual planning with your pandit, granthi or qazi",
            "An on-ground team from the first check-in to the last checkout"
        ]
    },
    {
        id: 'partial-planning',
        name: "Partial planning",
        tagline: "You have made a start. We bring it all together and see it through.",
        description: "Already booked your venue and a few vendors? We step in to fill the gaps, design the functions you have not planned yet and take over coordination, so the last six months feel lighter.",
        bestFor: "Couples who have their venue and some vendors confirmed, and want experts for the rest.",
        leadTime: "Book 5 to 8 months ahead",
        includes: [
            "A planning review of everything booked so far",
            "Design and vendors for up to three functions",
            "Budget review and vendor negotiation",
            "Guest management and RSVP tracking",
            "Detailed timelines for every function",
            "On-ground coordination through the wedding week"
        ]
    },
    {
        id: 'wedding-management',
        name: "Wedding week management",
        tagline: "You planned it beautifully. We make sure it runs that way.",
        description: "For families who have planned everything themselves. We take over eight weeks before the wedding, confirm every vendor, build the run of show and run each function on the ground.",
        bestFor: "Families who have done the planning and want a professional team to run the days.",
        leadTime: "Book 2 to 4 months ahead",
        includes: [
            "Handover meetings eight weeks before the wedding",
            "Vendor confirmations and a payment schedule",
            "A minute-by-minute run of show for each function",
            "Rehearsals for the sangeet and entries",
            "Guest arrivals and a hospitality desk",
            "A coordination team at every function"
        ]
    },
    {
        id: 'destination-weddings',
        name: "Destination weddings",
        tagline: "Palaces, beaches, backwaters or abroad. We know the venues and the logistics.",
        description: "Destination weddings are logistics with a view. We handle venue contracts, permissions, flights, transfers and local vendors in India and abroad, so 300 guests arrive, celebrate and fly home without a single missed pickup.",
        bestFor: "Couples bringing families together in Udaipur, Goa, Jaipur, Kerala, Dubai, Bali or Thailand.",
        leadTime: "Book 12 to 18 months ahead",
        includes: [
            "Venue scouting trips and contract negotiation",
            "Local permissions and licences",
            "Guest flights, room blocks and transfers",
            "Welcome hampers and itineraries for every guest",
            "Local and travelling vendor teams",
            "Weather and backup venue planning"
        ]
    }
]

export type Inclusion = {
    title: string
    description: string
    icon: LucideIcon
}

export const INCLUSIONS: Array<Inclusion> = [
    {
        title: "A senior lead planner",
        description: "One person who knows your wedding inside out, and who answers when you call.",
        icon: UsersRound
    },
    {
        title: "One shared plan",
        description: "Budgets, timelines and vendor details in one place your families can see.",
        icon: ClipboardList
    },
    {
        title: "Family coordination",
        description: "Calls with parents and elders, so decisions do not get stuck in the family group chat.",
        icon: HeartHandshake
    },
    {
        title: "Honest vendor pricing",
        description: "Vendor quotes are shared with you exactly as we receive them. No hidden mark-ups.",
        icon: Wallet
    },
    {
        title: "Contingency planning",
        description: "Rain plans, power backups and a spare everything, from safety pins to sherwani buttons.",
        icon: ShieldCheck
    },
    {
        title: "A tidy wrap-up",
        description: "Vendor settlements, returns and a final account, so nothing follows you home.",
        icon: Sparkles
    }
]

export type ProcessStep = {
    title: string
    description: string
}

export const PROCESS: Array<ProcessStep> = [
    {
        title: "Discovery call",
        description: "A relaxed 45-minute conversation with you, and whoever else is deciding, about your families, the feel you want and your non-negotiables."
    },
    {
        title: "Proposal and budget",
        description: "Within a week, a written proposal with venue ideas, a function-by-function budget and our planning fee."
    },
    {
        title: "Design and vendors",
        description: "Mood boards for every function and a shortlist of decorators, caterers, photographers and artists we trust."
    },
    {
        title: "Guests and logistics",
        description: "RSVPs, room blocks, flights, transfers and welcome hampers, all tracked in one place."
    },
    {
        title: "The wedding week",
        description: "Our team on the ground from the first check-in to the vidaai, working from a minute-by-minute plan."
    }
]

export type Faq = {
    question: string
    answer: string
}

export const FAQS: Array<Faq> = [
    {
        question: "How far in advance should we book?",
        answer: "For full planning, 10 to 14 months gives us the best choice of venues and dates, especially for the November to February season. Shorter timelines are often possible, and we will tell you honestly what can be done on our first call."
    },
    {
        question: "Do you only plan Hindu weddings?",
        answer: "Not at all. We plan Hindu, Sikh, Muslim, Jain, Christian, Parsi and interfaith weddings, and weddings that blend the traditions of two families. Rituals are planned with your pandit, granthi, qazi or priest, the way your families actually do them."
    },
    {
        question: "How do your fees work?",
        answer: "Our planning fee is a fixed amount agreed in your proposal, based on the number of functions, the guest count and the destination. Vendor quotes are passed on to you exactly as we receive them."
    },
    {
        question: "Can we bring our own vendors?",
        answer: "Of course. If your family has a favourite caterer, mehendi artist or pandit, we bring them into the plan and coordinate with them like any other member of the team."
    },
    {
        question: "Do you plan weddings outside India?",
        answer: "Yes. We plan weddings in Dubai, Bali, Thailand, Sri Lanka and Italy, including guest travel, visa invitation letters and local vendors."
    },
    {
        question: "Can you manage more than 1,000 guests?",
        answer: "Yes. Larger weddings need more people, not more stress. We scale the on-ground team, hospitality desks and transport to your guest list, and plan how guests move through every venue."
    },
    {
        question: "What happens on the first call?",
        answer: "We talk about you, your families and the wedding you are imagining. There is no charge and no obligation, and you will have a written proposal within a week of the call."
    }
]
